import { useState } from "react"
import type { PodTierId } from "~/content/pods"
import { ResponsiveImage } from "~/content/responsive-image"
import { teamMemberIds, teamMembers } from "~/content/team"
import { BrandBand } from "~/marketing/brand-band"
import { PodComparison } from "~/marketing/pod-comparison"
import { PodEffortPreview } from "~/marketing/pod-effort-preview"
import { ScheduleLink } from "~/marketing/schedule-link"

export const PodsPage = () => {
  const [selectedTier, setSelectedTier] = useState<PodTierId>("medium")

  return (
    <>
      <section className="container marketing-hero grid gap-10 md:grid-cols-2 md:items-start md:gap-12">
        <div className="min-w-0">
          <h1 className="home-sans-heading marketing-page-title marketing-split-title">
            <span className="headline-beat">Turn your roadmap</span>{" "}
            <span className="headline-beat">
              into <span className="home-emphasis home-highlight">a product.</span>
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Work with a senior product team month to month. Bring us an idea, a prototype, or a
            product you want to improve. Together, we decide what to build next. We handle the
            design, development, and testing, and put working software in front of you to try and
            shape as we go.
          </p>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Pick your speed: Low, Medium, High or Max. Turn it up, turn it down, or stop at the end
            of any month.
          </p>
        </div>
        <div className="max-w-xl">
          <PodEffortPreview initial={selectedTier} onChoose={setSelectedTier} />
        </div>
      </section>
      <BrandBand />

      <PodComparison selected={selectedTier} />

      <div className="pod-story">
        <section className="pod-story-section container space-y-8">
          <h2 className="home-sans-heading marketing-section-heading">
            From your next priority
            <br className="hidden sm:block" /> to working software.
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            We work in rounds of building and feedback. Each round starts with a clear priority and
            gives you something to try, respond to, and build on.
          </p>
          <ol className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {process.map(([title, body]) => (
              <li key={title} className="space-y-3 border-l-2 border-primary pl-5">
                <h3 className="text-xl leading-snug sm:text-2xl">{title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{body}</p>
              </li>
            ))}
          </ol>
          <p className="text-lg leading-relaxed">
            The cycle repeats at your chosen effort level. Feedback shapes what comes next.
          </p>
        </section>

        <section className="marketing-surface-dark text-secondary-foreground">
          <div className="pod-story-section container grid items-start gap-10 lg:grid-cols-[530fr_742fr] lg:gap-16">
            <div className="space-y-6">
              <h2 className="home-sans-heading marketing-section-heading">
                Something you can try.
              </h2>
              <p className="text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
                A batch is a set of related features or improvements that we build for you to review
                together. We agree on the scope around a useful outcome.
              </p>
              <p className="text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
                The size depends on the work. The point is to put progress in your hands so your
                feedback can shape the next round.
              </p>
            </div>
            <article className="space-y-5 rounded-2xl bg-secondary-foreground/10 px-6 py-7 sm:px-8">
              <p className="marketing-eyebrow text-primary">For example</p>
              <h3 className="text-2xl leading-snug sm:text-3xl">
                Help new users finish signing up.
              </h3>
              <p className="text-lg leading-relaxed text-secondary-foreground/80">
                One batch could bring together a simpler signup flow, clearer form screens, the
                development behind them, and testing.
              </p>
              <p className="text-lg leading-relaxed text-secondary-foreground/80">
                You try the working flow. We review your feedback together, make adjustments, and
                decide what to tackle next.
              </p>
            </article>
          </div>
        </section>

        <section
          className="pod-story-section container space-y-8"
          aria-labelledby="pod-team-heading"
        >
          <h2 id="pod-team-heading" className="home-sans-heading marketing-section-heading">
            A team you work with directly.
          </h2>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            CTO-level leadership and a team that can take your product from decisions and design
            through development, testing, and release.
          </p>
          <div className="grid items-start gap-12 lg:grid-cols-[424fr_840fr] lg:gap-[72px]">
            <ul className="grid grid-cols-2 gap-x-6 gap-y-[18px]">
              {teamMemberIds.map((id) => {
                const person = teamMembers[id]
                const name = id === "david-renz" ? "Dave" : person.name.split(" ")[0]
                return (
                  <li key={id} className="flex min-w-0 flex-col items-center gap-[5px] text-center">
                    <ResponsiveImage
                      assetId={person.imageAssetId}
                      alt={name}
                      sizes="avatar"
                      className="size-[88px] rounded-full object-contain"
                    />
                    <p className="text-[17px] font-bold leading-[1.4]">{name}</p>
                    <p className="max-w-[200px] text-[15px] leading-[1.25]">{person.title}</p>
                  </li>
                )
              })}
            </ul>
            <div className="space-y-7">
              {capabilities.map(([title, body]) => (
                <div key={title} className="space-y-1">
                  <h3 className="text-xl leading-snug sm:text-2xl">{title}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="marketing-surface-dark text-secondary-foreground">
          <div className="pod-story-section container space-y-8">
            <h2 className="home-sans-heading marketing-section-heading">
              A rhythm that fits your pace.
            </h2>
            <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
              {rhythm.map(([title, body, more]) => (
                <div key={title} className="space-y-4">
                  <h3 className="text-xl leading-snug sm:text-2xl">{title}</h3>
                  <p className="text-lg leading-relaxed text-secondary-foreground/80">{body}</p>
                  <p className="text-lg leading-relaxed text-secondary-foreground/80">{more}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pod-story-section container space-y-8">
          <h2 className="home-sans-heading marketing-section-heading">A few things to know.</h2>
          <dl className="space-y-8">
            {faqs.map(([question, answer]) => (
              <div key={question} className="grid gap-3 md:grid-cols-[400fr_888fr] md:gap-12">
                <dt className="text-xl font-semibold leading-snug sm:text-2xl">{question}</dt>
                <dd className="text-lg leading-relaxed text-muted-foreground sm:text-xl">
                  {answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="marketing-surface-dark text-center text-secondary-foreground">
          <div className="pod-story-section container space-y-8">
            <h2 className="home-sans-heading marketing-section-heading">
              Let’s talk about your product.
            </h2>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
              Bring your idea, your current product, or the next thing you want to build. We’ll talk
              through where to start and which effort level makes sense.
            </p>
            <ScheduleLink
              ctaLocation="pods_medium"
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-primary px-6 text-[18.66px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Book a call with Dave or Eric →
            </ScheduleLink>
          </div>
        </section>
      </div>
    </>
  )
}

const process = [
  [
    "Choose the next priority",
    "Together, we decide what matters most for your users and agree on a manageable piece of work.",
  ],
  ["Design and build", "We work through the experience, develop it, and test how it behaves."],
  [
    "Try it together",
    "You use the working version and share what fits, what is missing, and what needs to change.",
  ],
  [
    "Refine and release",
    "We address feedback, prepare the work for release, and use what we learn to choose the next priority.",
  ],
] as const

const capabilities = [
  [
    "Technical leadership",
    "Dave and Eric bring CTO-level guidance to your product, architecture, and technical decisions.",
  ],
  [
    "Product management",
    "Turn your goals and customer feedback into clear priorities and a plan for what comes next.",
  ],
  [
    "UI/UX design",
    "Work through how your product should feel and function, from user flows to the details on each screen.",
  ],
  ["Development", "Build the features, integrations, and foundations your product needs."],
  [
    "QA testing",
    "Test the work, catch issues, and check that the experience holds together before release.",
  ],
  [
    "Agent workflows",
    "Direct AI agents throughout development, evaluate their output, and improve how the work gets done.",
  ],
] as const

const rhythm = [
  [
    "Getting started",
    "We look at your idea or existing product, understand your users, and identify what needs to happen first.",
    "Together, we choose the first priority and agree on the effort level.",
  ],
  [
    "Between meetings",
    "The team keeps designing, building, and testing. Shared Slack gives us a place for questions and feedback as the work develops.",
    "Your product meetings are where we review progress and decide what comes next.",
  ],
  [
    "Your part in the process",
    "Bring your knowledge of your customers. Make time to try the work, share feedback, and help us choose priorities.",
    "If the pace needs to change, we work through it together.",
  ],
] as const

const faqs = [
  ["Who owns the code?", "You do, from day one. It lives in your repository."],
  [
    "Can we change effort or stop?",
    "Yes. Everything is month to month. Change your speed or stop at the end of any month.",
  ],
  [
    "Can you work on an existing product?",
    "Yes. We look at the codebase and deployment setup together before agreeing on where to start.",
  ],
  [
    "What if we need more time to give feedback?",
    "Tell us. We can hold the next batch while you catch up and discuss a different effort level for the following month.",
  ],
  [
    "How is security handled?",
    "Every effort level includes a monthly security review, alongside testing as we build.",
  ],
] as const
